/**
 * ENTRY POINT. The order of the four startup steps is the content of this file.
 * ===========================================================================
 *
 *  1. **Recover stranded work FIRST, before React renders.** `recoverInFlight()` returns any row left
 *     `in-flight` by the app being killed mid-request back to `queued`. Doing it before the first
 *     paint means the technician never sees a "sending…" row that is not actually being sent. It is
 *     safe to retry because the idempotency key is stable — a request that did land dedupes
 *     server-side.
 *
 *  2. **Ask for persistent storage.** Started here rather than inside a component so the request goes
 *     out during startup rather than after a route change, and so a component remount cannot re-ask.
 *     The ANSWER is rendered by the shell, honestly, including when it is "no".
 *
 *  3. **Render.**
 *
 *  4. **Register the service worker last, and only in a production build.** Last, because
 *     registration competes for the same main thread as the first paint. Production-only, because a
 *     precaching worker in front of Vite's dev server serves yesterday's module graph and produces
 *     bugs that do not exist — `import.meta.env.PROD` is the guard, and the offline path is verified
 *     with `npm run build && npm run preview` instead.
 *
 * ⚠️ **STEP 0, ADDED: `installOidcAuth()`.** Sign-in is now built (`src/auth/`), and it is installed
 * FIRST — before `recoverInFlight`, before the first paint — because a callback URL carries an
 * authorization code that must be exchanged before anything asks for a token, and because leaving the
 * `code` in the address bar for even one paint invites a reload that re-submits it.
 *
 * **It is expected to report `not-configured` today, and that is the correct behaviour, not a
 * regression.** No app client exists on the Federate service profile yet. When the configuration is
 * incomplete, `installOidcAuth` does **not** call `setTokenProvider`: the stub from `core/auth.ts`
 * stays active, `api.ts` keeps answering `auth-not-configured`, and **the app keeps sending nothing at
 * all** rather than firing requests that would come back 401 and read like an expired session. The
 * failure is loud — a `console.error` naming every missing variable — rather than inferred from an app
 * that quietly does nothing.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { installOidcAuth } from './auth';
import { recoverInFlight } from './core/outbox';
import { assessStorage } from './core/storage';
import './ui/styles.css';

async function start(): Promise<void> {
  /*
   * Awaited, and deliberately not `void`ed: if this load IS a sign-in callback, the code exchange has to
   * finish before the outbox flusher can possibly ask for a token. It cannot throw — every failure path
   * inside returns a result — and it is cheap when the URL is an ordinary app load, where it does not
   * even touch the address bar.
   */
  await installOidcAuth();

  try {
    const recovered = await recoverInFlight();
    if (recovered > 0) {
      // eslint-disable-next-line no-console -- a recovered item means the app died mid-send. Loud.
      console.warn(`[lockercustody] returned ${recovered} interrupted submission(s) to the queue`);
    }
  } catch (error) {
    // A failure here means IndexedDB is unusable. The app still renders — and the storage banner
    // will report the problem — because a blank screen tells the technician nothing.
    // eslint-disable-next-line no-console -- this is the loudest surface available at this point.
    console.error('[lockercustody] could not read the outbox on startup', error);
  }

  void assessStorage();

  const container = document.getElementById('root');
  if (container === null) {
    throw new Error('#root missing from index.html');
  }
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    // `void` and no `await`: a registration failure must not stop the app, which works offline out of
    // IndexedDB regardless of whether the shell is precached.
    /*
     * Registered relative to `BASE_URL`, not hardcoded to `/`.
     *
     * A service worker's scope cannot rise above the directory it is served from, so a hardcoded
     * `/sw.js` 404s the moment the app is hosted anywhere but a domain root — which is exactly what
     * happens on an internal prototype host serving from `/prototypes/<guid>/`. The `.catch` below
     * meant that failed *silently enough* to look like "offline just doesn't work here", with the
     * app otherwise fine, so the cause would not have been obvious.
     *
     * Vite substitutes `BASE_URL` from the `base` the bundle was built with, so one build works at a
     * root and a subpath without a code change, and `scope` matches wherever it landed.
     */
    const swUrl = `${import.meta.env.BASE_URL}sw.js`.replace(/\/{2,}/g, '/');
    void navigator.serviceWorker.register(swUrl, { scope: import.meta.env.BASE_URL }).catch((error: unknown) => {
      // eslint-disable-next-line no-console -- silent here would mean silently losing offline launch.
      console.error('[lockercustody] service worker registration failed', error);
    });
  }
}

void start();
